<template>

    <main class="_article pt-8 pb-8">
        <article class="prose p-3 max-w-7xl m-auto">
            <div class="mb-2 text-sm lg:px-44 flex items-center justify-between">
                <NuxtLink href="/blog" class="text-sm py-1 px-2 no-underline btn btn-sm btn-gray flex items-center">
                    <Icon name="material-symbols:arrow-back" class="mr-1" />
                    All articles
                </NuxtLink>
                <Share v-if="article" class="inline-block ml-2" :title="article?.title"
                    :summary="article?.description" />
                <Feedback class="inline-block" />
            </div>

            <div v-if="article" class="content-doc mt-8 lg:px-44">
                <h1>{{ article.title }}</h1>
                <!-- Single labelled date block directly under the H1 (TICKET-10887). -->
                <p v-if="publishedDate" class="text-sm text-gray-400">
                    <time :datetime="publishedDate">Published {{ formattedDate }}</time>
                    <template v-if="showUpdatedDate">
                        &middot; <time :datetime="updatedDate">Last updated {{ formattedUpdatedDate }}</time>
                    </template>
                </p>
                <ContentRenderer v-if="renderedArticle" :value="renderedArticle" />
            </div>

            <div class="mb-2 mt-8 text-sm lg:px-44 flex items-center justify-between">
                <NuxtLink href="/blog" class="text-sm py-1 px-2 no-underline btn btn-sm btn-gray flex items-center"
                    style="text-decoration:none">
                    <Icon name="material-symbols:arrow-back" class="mr-1" />
                    All articles
                </NuxtLink>
                <Share v-if="article" class="inline-block ml-2" :title="article?.title"
                    :summary="article?.description" />
                <Feedback class="inline-block" />
            </div>
        </article>
    </main>
</template>

<script lang="ts" setup>
import type { TMinimarkNode } from '~/composables/useBlogStructuredData';

const route = useRoute();

definePageMeta({
    alias: ['/help/:slug'],
});

// Path must be unique per URL: a static useAsyncData key dedupes across all blog posts and
// SSR/prerender can serve the wrong article for every slug.
const contentPath = route.path.replace(/^\/help\//, '/blog/');
const { data: article } = await useAsyncData(
    `article-meta:${contentPath}`,
    () => queryCollection('content').path(contentPath).first()
);

// Explicit metaTitle/metaDescription (from Notion, once the admin articles API
// surfaces them — see helpcenter.ts) win when present; otherwise fall back to
// the derived title/description, tightened to SERP-safe lengths. Never emit an
// untrimmed H1-length title or an over-length description (TICKET-10023).
const seoTitle = computed(() => {
    const raw = resolveMetaText(article.value?.metaTitle || article.value?.title);
    return raw ? tightenSeoTitle(raw, ' | Unified.to') : 'Blog | Unified.to';
});
const seoDescription = computed(() => {
    const raw = resolveMetaText(article.value?.metaDescription || article.value?.description);
    return raw ? trimSeoDescription(raw) : undefined;
});

// Compute canonical URL and absolute image URL for OG/Twitter tags
const siteBaseUrl = 'https://unified.to';
// Prefer contentPath (maps /help/:slug → /blog/:slug) so aliases share one canonical.
// Use path only (not fullPath) so query/hash never leak — same trim as useSEO.ts.
const canonicalUrl = computed(() => {
    const path = contentPath === '/' ? '/' : contentPath.replace(/\/+$/, '');
    return `${siteBaseUrl}${path}`;
});
const ogImageUrl = computed(() => {
    const src = article.value?.img || '/logos/Unified_Black.png';
    return /^https?:\/\//i.test(src) ? src : new URL(src, siteBaseUrl).toString();
});
// Per-article image dimensions aren't known ahead of time, so only publish
// og:image:type (inferred from the file extension) and og:image:alt — never
// og:image:width/height, which would misreport the actual image size.
const ogImageType = computed(() => {
    const ext = ogImageUrl.value.split('.').pop()?.toLowerCase().split(/[?#]/)[0];
    const types: Record<string, string> = {
        png: 'image/png',
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        webp: 'image/webp',
        gif: 'image/gif',
        svg: 'image/svg+xml',
    };
    return ext ? types[ext] : undefined;
});
const publishedDate = computed(() => {
    if (!article.value?.date) {
        return undefined;
    }

    const parsedDate = new Date(article.value.date);
    if (Number.isNaN(parsedDate.getTime())) {
        return undefined;
    }

    return parsedDate.toISOString().split('T')[0];
});
const formattedDate = computed(() => {
    if (!publishedDate.value) return '';
    const d = new Date(publishedDate.value + 'T00:00:00');
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
});
const updatedDate = computed(() => {
    if (!article.value?.updated) {
        return undefined;
    }

    const parsedDate = new Date(article.value.updated);
    if (Number.isNaN(parsedDate.getTime())) {
        return undefined;
    }

    return parsedDate.toISOString().split('T')[0];
});
const formattedUpdatedDate = computed(() => {
    if (!updatedDate.value) return '';
    const d = new Date(updatedDate.value + 'T00:00:00');
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
});
// Only surface "Last updated" when it's meaningfully after the original publish date.
const showUpdatedDate = computed(() => !!updatedDate.value && !!publishedDate.value && updatedDate.value > publishedDate.value);
// dateModified / article:modified_time must equal what the page shows: the "Last updated"
// date when it is displayed, otherwise the publish date.
const modifiedDate = computed(() => (showUpdatedDate.value ? updatedDate.value : publishedDate.value));

// The synced Markdown starts with `# Title`, an <hr> and an unlabelled italic publish date.
// The template renders the H1 and the labelled Published / Last updated line itself, so drop
// those leading nodes from the body to avoid a second, unlabelled date (TICKET-10887).
const renderedArticle = computed(() => {
    const body = article.value?.body;
    if (!article.value || !body || !Array.isArray(body.value)) return article.value;
    let nodes = body.value;
    if (Array.isArray(nodes[0]) && nodes[0][0] === 'h1') {
        nodes = nodes.slice(1);
        if (Array.isArray(nodes[0]) && nodes[0][0] === 'hr') nodes = nodes.slice(1);
        const first = nodes[0];
        if (Array.isArray(first) && first[0] === 'p' && first.length === 3 && Array.isArray(first[2]) && first[2][0] === 'em') {
            nodes = nodes.slice(1);
        }
    }
    return { ...article.value, body: { ...body, value: nodes } };
});
const articleJsonLd = computed(() => ({
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': canonicalUrl.value,
    },
    url: canonicalUrl.value,
    headline: resolveMetaText(article.value?.title),
    description: seoDescription.value,
    datePublished: publishedDate.value,
    dateModified: modifiedDate.value,
    image: ogImageUrl.value,
    author: article.value?.author
        ? {
              '@type': 'Person',
              name: article.value.author,
              ...(article.value.authorUrl ? { url: article.value.authorUrl } : {}),
          }
        : {
              '@type': 'Organization',
              name: 'Unified.to',
              url: 'https://unified.to',
          },
    publisher: {
        '@type': 'Organization',
        name: 'Unified.to',
        url: 'https://unified.to',
        logo: {
            '@type': 'ImageObject',
            url: 'https://unified.to/logos/Unified_Black.png',
        },
    },
}));

// FAQPage markup only for posts with a "Frequently asked questions" section.
const faqJsonLd = computed(() => buildFaqPageJsonLd(extractFaqFromMarkdown(markdownFromMinimark(article.value?.body?.value as TMinimarkNode[] | undefined))));

// Set full Open Graph and Twitter metadata per article
useHead(() => ({
    title: seoTitle.value,
    link: [
        { rel: 'canonical', href: canonicalUrl.value },
    ],
    meta: [
        { name: 'description', content: seoDescription.value },
        { property: 'og:title', content: seoTitle.value },
        { property: 'og:description', content: seoDescription.value },
        { property: 'og:image', content: ogImageUrl.value },
        ...(ogImageType.value ? [{ property: 'og:image:type', content: ogImageType.value }] : []),
        { property: 'og:image:alt', content: article.value?.title || 'Unified.to' },
        { property: 'og:type', content: 'article' },
        { property: 'og:url', content: canonicalUrl.value },
        { property: 'article:published_time', content: publishedDate.value },
        { property: 'article:modified_time', content: modifiedDate.value },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: seoTitle.value },
        { name: 'twitter:description', content: seoDescription.value },
        { name: 'twitter:image', content: ogImageUrl.value },
    ],
    script: [
        {
            type: 'application/ld+json' as const,
            key: 'article-structured-data',
            textContent: JSON.stringify(articleJsonLd.value),
        },
        ...(faqJsonLd.value
            ? [{ type: 'application/ld+json' as const, key: 'faq-structured-data', textContent: JSON.stringify(faqJsonLd.value) }]
            : []),
    ],
}));
</script>

<style></style>